// Phase 2Z.n — Coverage Guard V1
//
// Pure JS evaluator for Coverage Guard. NO React, NO async, NO
// network calls — every input comes from data the calling surface
// already has in memory. Three call shapes:
//
//   evaluateRotaStyleCoverage({ cells, employees, categories, rules,
//                                cycle_days })
//     -> { overall: "green" | "red" | "grey",
//          perRule: [{ rule, status, perDay, unsupported, reason }],
//          unsupportedRulesCount, totalEvaluatedCount }
//
//   simulateRmiAdjustMix({ cells, toFlip, fromCategoryId, toCategoryId })
//     -> nextCells  (in-memory only; no PUT, no setCells, no api call)
//
//   evaluateRosterCoverage({ perRulePreviews })
//     -> { overall, perRule } — wraps the existing
//        POST /api/coverage-rules/{id}/preview response.
//
// V1 supported fields on each rule:
//   - is_active
//   - category_id
//   - team_id (None = all teams)
//   - days_of_week (array of 0..6)
//   - min_headcount
//   - min_coverage_pct
//   - name, description, coverage_id (display only)
//
// V1 UNSUPPORTED fields (treated as "not-fully-checked" so the badge
// can never go falsely green when these are present):
//   - max_headcount, max_coverage_pct
//   - start_time, end_time (time-of-day window)
//   - location_id
//   - effective_from, effective_to (for Rota Style templates only)
//   - apply_per_team
//   - priority (silently ignored — equal weighting in V1)
//
// CG time-window Phase 1 (June 2026): start_time / end_time BECOME
// supported when (and only when) the rule explicitly opted in via
// coverage_period === "time_window". Unflagged rules - including
// legacy rules that already carry times - keep the exact behaviour
// above (times still mean "not fully checked").
// Binary contract (June 2026): a flagged window rule always resolves
// to Covered ("ok") or Under-covered ("under"). Timing that cannot
// be read contributes no cover - it is never guessed and never shown
// as a separate vague status.

// Fields a rule is allowed to carry while still being "fully
// V1-supported". Anything else makes a rule "not-fully-checked".
// Note: `apply_per_team` IS V1-supported (Phase 2Z.n.1 — "Each
// team" scope), so it's deliberately absent from this list.
const V1_UNSUPPORTED_FIELDS = [
  "max_headcount",
  "max_coverage_pct",
  "start_time",
  "end_time",
  "location_id",
  "effective_from",
  "effective_to",
];

export function ruleUsesUnsupportedV1Fields(rule) {
  if (!rule) return false;
  const windowRule = isTimeWindowRule(rule);
  for (const f of V1_UNSUPPORTED_FIELDS) {
    // Time-window Phase 1: start/end times are supported when the
    // rule explicitly opted in. Unflagged rules keep the exact
    // legacy result.
    if (windowRule && (f === "start_time" || f === "end_time")) continue;
    const v = rule[f];
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v === "") continue;
    if (typeof v === "boolean" && v === false) continue;
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// CG time-window Phase 1 (June 2026) - continuous cover kernel.
//
// A rule only enters time-window evaluation when a manager has
// EXPLICITLY opted in via coverage_period === "time_window" AND the
// rule carries a valid same-day window (start < end). The sweep
// kernel below MUST stay in lockstep with _cg_window_sweep in
// backend/server.py (shared vectors in
// backend/tests/test_cg_time_window_phase1.py).

function hhmmToMin(v) {
  if (!v || typeof v !== "string") return null;
  const parts = v.split(":");
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

export function isTimeWindowRule(rule) {
  if (!rule || rule.coverage_period !== "time_window") return false;
  const s = hhmmToMin(rule.start_time);
  const e = hhmmToMin(rule.end_time);
  return s !== null && e !== null && e > s;
}

function mergeSegments(segs) {
  const out = [];
  const sorted = segs.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const [s, e] of sorted) {
    if (out.length > 0 && s <= out[out.length - 1][1]) {
      out[out.length - 1][1] = Math.max(out[out.length - 1][1], e);
    } else {
      out.push([s, e]);
    }
  }
  return out;
}

// Interval sweep over [windowStart, windowEnd) in minutes.
// employeeSegments: one array of [start, end] segments per employee
// whose cover is verifiable. ambiguousCount: employees assigned to
// the category that day whose timing cannot be verified.
export function sweepWindowCoverage({
  windowStart, windowEnd, minHeadcount, employeeSegments, ambiguousCount,
}) {
  const events = [];
  for (const segs of (employeeSegments || [])) {
    const clipped = [];
    for (const [s, e] of segs) {
      const cs = Math.max(s, windowStart);
      const ce = Math.min(e, windowEnd);
      if (ce > cs) clipped.push([cs, ce]);
    }
    for (const [s, e] of mergeSegments(clipped)) {
      events.push([s, 1]);
      events.push([e, -1]);
    }
  }
  const pointSet = new Set([windowStart, windowEnd]);
  for (const [t] of events) pointSet.add(t);
  const points = Array.from(pointSet).sort((a, b) => a - b);
  const deltas = new Map();
  for (const [t, d] of events) deltas.set(t, (deltas.get(t) || 0) + d);
  let count = 0;
  let coveredOk = 0;
  let possibleOk = 0;
  let worst = null;
  const gaps = [];
  const amb = ambiguousCount || 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p = points[i];
    const q = points[i + 1];
    count += deltas.get(p) || 0;
    const span = q - p;
    worst = worst === null ? count : Math.min(worst, count);
    if (count >= minHeadcount) coveredOk += span;
    if (count + amb >= minHeadcount) possibleOk += span;
    if (count < minHeadcount) {
      const shortfall = minHeadcount - count;
      const last = gaps[gaps.length - 1];
      if (last && last.end === p && last.shortfall === shortfall) {
        last.end = q;
      } else {
        gaps.push({ start: p, end: q, shortfall });
      }
    }
  }
  const gapMinutes = (windowEnd - windowStart) - coveredOk;
  const maxShortfall = gaps.reduce((m, g) => Math.max(m, g.shortfall), 0);
  const covered = gapMinutes === 0;
  let status;
  if (covered) status = "ok";
  else if (amb > 0 && maxShortfall <= amb) status = "not-fully-checked";
  else status = "under";
  return {
    covered, status, gapMinutes,
    coveredOkMinutes: coveredOk, possibleOkMinutes: possibleOk,
    maxShortfall, worstHeadcount: worst === null ? 0 : worst, gaps,
  };
}

// Verifiable cover one cell contributes to a time-window rule.
// Binary contract (June 2026): returns null when the cell is
// irrelevant to the rule's category, otherwise { segments }.
// Assignment-row times win: an assignment's own start/end (or start
// + minutes) defines exactly when the category is covered. An
// untimed assignment falls back to the cell window (minus the
// break) ONLY when the whole cell is a single work category.
// Anything unreadable contributes NO cover - never guessed, never a
// separate "not fully checked" state. Overnight windows are clipped
// at midnight. Unplaced breaks default to the shift midpoint - the
// same convention _default_break_start uses when real shifts are
// written. MUST stay in lockstep with _cg_employee_day_segments in
// backend/server.py.
function cgWindowCellContribution(cell, kindByCategoryId, categoryId) {
  const assigns = cell.assignments || [];
  const workAssigns = assigns.filter(
    (a) => kindByCategoryId.get(a.category_id) !== "away");
  const catAssigns = workAssigns.filter(
    (a) => a.category_id === categoryId);
  if (catAssigns.length === 0) return null;
  const segments = [];
  let hasUntimed = false;
  for (const a of catAssigns) {
    const s = hhmmToMin(a.start_time);
    let e = hhmmToMin(a.end_time);
    if (s !== null && e === null && Number(a.minutes) > 0) {
      e = s + Number(a.minutes);
    }
    if (s === null || e === null) { hasUntimed = true; continue; }
    if (e === s) continue;
    if (e < s) e += 24 * 60;
    segments.push([s, Math.min(e, 24 * 60)]);
  }
  const distinctCats = new Set(workAssigns.map((a) => a.category_id));
  if (hasUntimed && distinctCats.size === 1) {
    const s = hhmmToMin(cell.start_time);
    let e = hhmmToMin(cell.end_time);
    if (s !== null && e !== null && e !== s) {
      if (e < s) e += 24 * 60;
      e = Math.min(e, 24 * 60);
      const bm = Math.max(0, cell.break_minutes || 0);
      if (bm <= 0) segments.push([s, e]);
      else {
        let bs = hhmmToMin(cell.break_start_time);
        if (bs === null) bs = Math.max(s, s + Math.floor((e - s - bm) / 2));
        const be = bs + bm;
        if (bs > s) segments.push([s, Math.min(bs, e)]);
        if (be < e) segments.push([Math.max(be, s), e]);
      }
    }
  }
  return { segments };
}

// Same net-minutes rule the C2/C3 preview + RMI engine uses. Falls
// back to the employee's working_pattern.daily_minutes (default 480)
// for full-day cells — matches Phase 2Z.m Option B byte-for-byte.
function cellNetMinutes(cell, employeeById) {
  const s = cell.start_time, e = cell.end_time;
  const validTime = s && e && e > s;
  if (validTime) {
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    const gross = (eh * 60 + em) - (sh * 60 + sm);
    const brk = Math.max(0, cell.break_minutes || 0);
    return Math.max(0, gross - brk);
  }
  const emp = employeeById.get(cell.employee_id);
  // daily_minutes is treated as NET (working) minutes — no further
  // break_minutes deduction (double-deduct guard).
  return emp?.working_pattern?.daily_minutes ?? 480;
}

// Per-rule day status — three states:
//   "ok"   — at least one of (headcount, pct) thresholds was met
//   "risk" — within 1 person OR within 5 pct points of threshold
//   "under" — both thresholds violated (or the only configured one)
function classifyDay(rule, assignedHeadcount, pct) {
  const hcConfigured = rule.min_headcount !== null
    && rule.min_headcount !== undefined;
  const pctConfigured = rule.min_coverage_pct !== null
    && rule.min_coverage_pct !== undefined;
  if (!hcConfigured && !pctConfigured) {
    // V1: no threshold configured — treat as ok (rule exists but
    // has nothing to enforce). The slim editor won't allow this.
    return "ok";
  }
  const hcOk = hcConfigured ? assignedHeadcount >= rule.min_headcount : null;
  const pctOk = pctConfigured ? pct >= rule.min_coverage_pct : null;
  // OR semantics — most permissive wins (matches the backend's
  // existing OR contract on min_headcount / min_coverage_pct).
  if (hcOk === true || pctOk === true) {
    // Day is OK — but flag "risk" if we're within striking distance.
    const hcClose = hcConfigured
      && assignedHeadcount < rule.min_headcount + 1
      && hcOk === false;
    const pctClose = pctConfigured
      && pct < rule.min_coverage_pct + 5
      && pctOk === false;
    if (hcClose || pctClose) return "risk";
    return "ok";
  }
  return "under";
}

export function evaluateRotaStyleCoverage({
  cells, employees, categories, rules, cycle_days, teams,
  scope_team_id,
}) {
  const empById = new Map();
  for (const e of employees || []) empById.set(e.employee_id, e);
  const activeCatIds = new Set(
    (categories || []).filter((c) => c.is_active !== false)
      .map((c) => c.category_id),
  );

  // Coverage Guard team scope (TP-CG.team) — when the calling
  // surface is viewing a team-scoped Rota Style profile, drop any
  // rule whose `team_id` is set AND does NOT match the current
  // scope. Without this, a UK-scoped rule evaluated against an
  // Italy-scoped profile sees zero matching employees and falsely
  // reports "under" coverage, leaking a UK warning into Italy.
  // Company-wide rules (team_id null/empty) and rules tied to THIS
  // team continue to evaluate normally. When `scope_team_id` is
  // null/undefined the page is showing the company-wide profile
  // and every rule remains in scope.
  const scopedRules = (rules || []).filter((r) => {
    if (!r) return false;
    if (!scope_team_id) return true;
    if (!r.team_id) return true;          // company-wide rule
    return r.team_id === scope_team_id;   // same-team rule
  });
  // Phase 2A Step 2 - kind map so Coverage Guard can exclude Away
  // assignment rows from the per-cell denominator. Without this,
  // a Rota Style cell carrying e.g. [Inbound, Break] would dilute
  // the Inbound rule's percentage by 1/(N+1). `cellNetMinutes`
  // already subtracts the cell-level break_minutes so dividing by
  // Work-only count keeps the maths consistent.
  const kindByCategoryId = new Map();
  for (const c of (categories || [])) {
    kindByCategoryId.set(c.category_id, c.kind || "work");
  }

  // Phase 2Z.n.1 — "Each team" scope. A rule with apply_per_team
  // is expanded BEFORE the main loop into N virtual sub-rules (one
  // per distinct team that has at least one employee). Each virtual
  // rule is evaluated as a regular team-scoped rule, so a single
  // under-covered team flips the shield to red regardless of how
  // well-covered the company is overall. The per-team rule rows
  // appear separately in the popover so admins can see WHICH team
  // is failing.
  //
  // Phase 2Z.n.3 — team names are resolved from the authoritative
  // /teams collection (passed in via the `teams` arg) so the
  // popover never shows raw team_id strings. Falls back to the
  // employee's own team_name field, then to "Unknown team".
  const distinctTeamIdsAll = Array.from(new Set(
    (employees || []).map((e) => e.team_id).filter(Boolean),
  ));
  // When the page is locked to a single team scope, "Each team"
  // (apply_per_team) rules collapse to evaluating against that one
  // team only — managers don't expect to see other teams' rows in
  // a team-scoped Rota Style popover.
  const distinctTeamIds = scope_team_id
    ? [scope_team_id]
    : distinctTeamIdsAll;
  const teamNameById = new Map();
  for (const t of (teams || [])) {
    if (t?.team_id && t.name) teamNameById.set(t.team_id, t.name);
  }
  for (const e of (employees || [])) {
    if (e.team_id && e.team_name && !teamNameById.has(e.team_id)) {
      teamNameById.set(e.team_id, e.team_name);
    }
  }
  const expandedRules = [];
  for (const r of scopedRules) {
    if (!r.is_active) continue;
    if (r.apply_per_team) {
      if (distinctTeamIds.length === 0) {
        // No teams to expand against — mark as not-fully-checked so
        // the badge can't go falsely green.
        expandedRules.push({
          ...r, _virtual_each_team: true,
          _no_teams: true,
        });
        continue;
      }
      for (const tid of distinctTeamIds) {
        expandedRules.push({
          ...r,
          team_id: tid,
          apply_per_team: false,  // already expanded; treat as regular
          _virtual_each_team: true,
          _each_team_parent_id: r.coverage_id,
          coverage_id: `${r.coverage_id}::team::${tid}`,
          name: `${r.name} - ${teamNameById.get(tid) || "Unknown team"}`,
        });
      }
    } else {
      expandedRules.push(r);
    }
  }

  let unsupportedRulesCount = 0;
  let totalEvaluatedCount = 0;
  const perRule = [];

  for (const rule of expandedRules) {
    // No-teams case for an Each-team rule that found no teams to
    // expand against.
    if (rule._no_teams) {
      perRule.push({
        rule, status: "not-fully-checked",
        unsupported: true,
        reason: "Each-team rule found no teams to evaluate",
        perDay: [],
      });
      unsupportedRulesCount += 1;
      continue;
    }
    // Skip rules that target a deactivated category — they aren't
    // V1-relevant. Treated as "not-fully-checked" so the badge
    // doesn't go falsely green from missing categories.
    if (!activeCatIds.has(rule.category_id)) {
      perRule.push({
        rule, status: "not-fully-checked",
        unsupported: true,
        reason: "Category is inactive",
        perDay: [],
      });
      unsupportedRulesCount += 1;
      continue;
    }
    if (ruleUsesUnsupportedV1Fields(rule)) {
      perRule.push({
        rule, status: "not-fully-checked",
        unsupported: true,
        reason: "Rule uses fields not supported in Coverage Guard V1 yet",
        perDay: [],
      });
      unsupportedRulesCount += 1;
      continue;
    }
    // V1-supported. Filter employees by team_id (if any).
    const empSet = rule.team_id
      ? new Set((employees || [])
          .filter((e) => e.team_id === rule.team_id)
          .map((e) => e.employee_id))
      : null;
    const dayFilter = (rule.days_of_week && rule.days_of_week.length > 0)
      ? new Set(rule.days_of_week) : null;
    // CG time-window Phase 1 - flagged rules take the continuous
    // window path. Binary contract (June 2026): every in-window day
    // is either verifiably covered ("ok") or it is not ("under").
    if (isTimeWindowRule(rule)) {
      const mn = rule.min_headcount;
      if (mn === null || mn === undefined) {
        // The editor requires a minimum for window rules; a rule
        // without one cannot be evaluated - say so clearly instead
        // of guessing.
        perRule.push({
          rule, status: "not-fully-checked", unsupported: true,
          reason: "Time-window rule needs a minimum number of people",
          perDay: [],
          timeWindow: { start: rule.start_time, end: rule.end_time },
        });
        unsupportedRulesCount += 1;
        continue;
      }
      const ws = hhmmToMin(rule.start_time);
      const we = hhmmToMin(rule.end_time);
      const perDayW = [];
      for (let d = 0; d < (cycle_days || 7); d += 1) {
        const dow = d % 7;
        if (dayFilter && !dayFilter.has(dow)) {
          perDayW.push({ day: d, dow, in_window: false, status: "ok",
                          assigned_headcount: 0, pct: 0 });
          continue;
        }
        const employeeSegments = [];
        for (const c of (cells || [])) {
          if (c.cycle_day_index !== d) continue;
          if (empSet && !empSet.has(c.employee_id)) continue;
          const contrib = cgWindowCellContribution(
            c, kindByCategoryId, rule.category_id);
          if (contrib === null) continue;
          if (contrib.segments.length > 0) {
            employeeSegments.push(contrib.segments);
          }
        }
        const sweep = sweepWindowCoverage({
          windowStart: ws, windowEnd: we, minHeadcount: mn,
          employeeSegments, ambiguousCount: 0,
        });
        perDayW.push({
          day: d, dow, in_window: true,
          status: sweep.status,
          assigned_headcount: sweep.worstHeadcount,
          gap_minutes: sweep.gapMinutes,
          pct: 0,
        });
      }
      const worstW = perDayW.some(
        (x) => x.in_window && x.status === "under") ? "under" : "ok";
      perRule.push({
        rule, status: worstW, unsupported: false, perDay: perDayW,
        timeWindow: { start: rule.start_time, end: rule.end_time },
      });
      totalEvaluatedCount += 1;
      continue;
    }
    const perDay = [];
    for (let d = 0; d < (cycle_days || 7); d += 1) {
      const dow = d % 7;
      if (dayFilter && !dayFilter.has(dow)) {
        perDay.push({ day: d, dow, in_window: false, status: "ok",
                       assigned_headcount: 0, pct: 0 });
        continue;
      }
      let totalCellMins = 0;
      let categoryCellMins = 0;
      const assignedEmps = new Set();
      for (const c of (cells || [])) {
        if (c.cycle_day_index !== d) continue;
        if (empSet && !empSet.has(c.employee_id)) continue;
        const assigns = c.assignments || [];
        if (assigns.length === 0) continue;
        // Phase 2A Step 2 - exclude Away rows from the denominator.
        // Net minutes already subtract the cell break; dividing by
        // Work-only count avoids diluting Work coverage when a cell
        // carries an Away/Break entry alongside Work assignments.
        const workAssigns = assigns.filter(
          (a) => kindByCategoryId.get(a.category_id) !== "away");
        const cellMins = cellNetMinutes(c, empById);
        totalCellMins += cellMins;
        const perAssign = workAssigns.length > 0
          ? cellMins / workAssigns.length : 0;
        for (const a of workAssigns) {
          if (a.category_id === rule.category_id) {
            categoryCellMins += perAssign;
          }
        }
        if (workAssigns.some((a) => a.category_id === rule.category_id)) {
          assignedEmps.add(c.employee_id);
        }
      }
      const pct = totalCellMins > 0
        ? (categoryCellMins / totalCellMins) * 100 : 0;
      const status = classifyDay(rule, assignedEmps.size, pct);
      perDay.push({
        day: d, dow, in_window: true,
        status,
        assigned_headcount: assignedEmps.size,
        pct: Math.round(pct * 10) / 10,
        total_cell_minutes: totalCellMins,
        category_cell_minutes: Math.round(categoryCellMins),
      });
    }
    const worst = perDay
      .filter((x) => x.in_window)
      .reduce((acc, x) => {
        if (acc === "under" || x.status === "under") return "under";
        if (acc === "risk" || x.status === "risk") return "risk";
        return "ok";
      }, "ok");
    perRule.push({
      rule, status: worst, unsupported: false, perDay,
    });
    totalEvaluatedCount += 1;
  }

  // Overall badge colour:
  // - grey  if zero V1-supported rules evaluated (regardless of how
  //   many unsupported rules sit there — never falsely green)
  // - red   if any V1-supported rule is "under"
  // - green only when every V1-supported rule is "ok" or "risk" AND
  //   there is at least one evaluated rule. Risk still counts as
  //   "needs attention" — surface it as red so managers can act.
  let overall;
  if (totalEvaluatedCount === 0) {
    overall = "grey";
  } else if (perRule.some((p) => !p.unsupported && p.status === "under")) {
    overall = "red";
  } else if (perRule.some((p) => !p.unsupported && p.status === "risk")) {
    overall = "red";  // V1 collapses risk -> red (single-action threshold)
  } else {
    overall = "green";
  }

  return { overall, perRule, unsupportedRulesCount, totalEvaluatedCount };
}

// Pure in-memory mirror of the C3 adjustMix kernel. The real engine
// in RotaStylePage.adjustMix() is the source of truth — this helper
// is locked to share the same predicate byte-for-byte via the test
// suite. NO PUT, NO setCells, NO api call.
//
// toFlip is the preview's own list: [{ cell, slotMinutes, action }, ...]
// where action is "flip" | "add" (the same metadata that drives the
// FLIP / ADD badges in the drawer).
export function simulateRmiAdjustMix({
  cells, toFlip, fromCategoryId, toCategoryId,
}) {
  if (!cells || !toFlip || toFlip.length === 0) return cells;
  const byCellId = new Map();
  for (const f of toFlip) {
    byCellId.set(f.cell.cell_id, f.action === "add" ? "add" : "flip");
  }
  return cells.map((c) => {
    if (!byCellId.has(c.cell_id)) return c;
    const assigns = c.assignments || [];
    const alreadyHasTo = assigns.some(
      (a) => a.category_id === toCategoryId);
    if (alreadyHasTo) return c;
    const action = byCellId.get(c.cell_id);
    if (action === "add") {
      const singleSlotFromOnly = (
        assigns.length === 1
        && assigns[0].category_id === fromCategoryId
      );
      if (!singleSlotFromOnly) return c;
      return {
        ...c,
        assignments: [
          ...assigns.map((a) => ({ category_id: a.category_id })),
          { category_id: toCategoryId },
        ],
      };
    }
    const containsFrom = assigns.some(
      (a) => a.category_id === fromCategoryId);
    if (!containsFrom) return c;
    let flipped = false;
    return {
      ...c,
      assignments: assigns.map((a) => {
        if (!flipped && a.category_id === fromCategoryId) {
          flipped = true;
          return { category_id: toCategoryId };
        }
        return { category_id: a.category_id };
      }),
    };
  });
}

// Coverage Guard for the Roster surface uses the existing backend
// preview endpoint per active rule. This is just a tiny pure helper
// that aggregates the responses into the same tri-state shape used
// elsewhere in this module.
//
// perRulePreviews: Array<{ rule, preview, error }>
//   - preview: { days: [{ gap, in_window, assigned_headcount, ... }],
//                 summary: { days_with_gap, max_gap, ... } }
//   - error: a string if the fetch failed for this rule.
export function summariseRosterCoverage(perRulePreviews) {
  const perRule = [];
  let evaluatedCount = 0;
  let unsupportedCount = 0;
  for (const item of (perRulePreviews || [])) {
    const { rule, preview, error } = item;
    if (error) {
      perRule.push({ rule, status: "not-fully-checked",
                      unsupported: true, reason: error, perDay: [] });
      unsupportedCount += 1;
      continue;
    }
    if (ruleUsesUnsupportedV1Fields(rule)) {
      perRule.push({ rule, status: "not-fully-checked",
                      unsupported: true,
                      reason: "Rule uses fields not supported in Coverage Guard V1 yet",
                      perDay: [] });
      unsupportedCount += 1;
      continue;
    }
    // CG time-window Phase 1 - flagged rules read the backend's
    // per-day `window_status`. Binary contract (June 2026): a window
    // day is either verifiably covered ("ok") or it is not ("under").
    // Missing / unknown status means the cover could not be read,
    // which counts as not covering. Unflagged rules keep the exact
    // legacy gap>0 mapping.
    const isWindow = isTimeWindowRule(rule);
    const perDay = (preview?.days || []).map((d) => {
      let status;
      if (isWindow) {
        status = d.in_window
          ? (d.window_status === "ok" ? "ok" : "under") : "ok";
      } else {
        status = d.in_window && d.gap > 0 ? "under" : "ok";
      }
      return {
        date: d.date, dow: d.day_of_week,
        in_window: !!d.in_window, status,
        assigned_headcount: (isWindow
          && d.window_worst_headcount !== null
          && d.window_worst_headcount !== undefined)
          ? d.window_worst_headcount : d.assigned_headcount,
        gap: d.gap || 0,
      };
    });
    const status = perDay.some((d) => d.status === "under")
      ? "under" : "ok";
    perRule.push({
      rule, status, unsupported: false, perDay,
      ...(isWindow
        ? { timeWindow: { start: rule.start_time, end: rule.end_time } }
        : {}),
    });
    evaluatedCount += 1;
  }
  let overall;
  if (evaluatedCount === 0) overall = "grey";
  else if (perRule.some((p) => !p.unsupported && p.status === "under")) {
    overall = "red";
  } else overall = "green";
  return { overall, perRule, unsupportedRulesCount: unsupportedCount,
            totalEvaluatedCount: evaluatedCount };
}

// Phase 2Z.n.2 — Coverage Guard Auto-Skip.
//
// Given a baseline cell set + an RMI preview's `toFlip` list, decide
// which proposed changes are SAFE (do not worsen Coverage Guard) and
// which are UNSAFE (would create undercoverage OR worsen existing
// undercoverage on at least one V1-supported rule day).
//
// Iterative cumulative simulation — order-deterministic. The first
// cell in toFlip is tentatively applied; if it worsens coverage on
// any V1-supported rule day, it's marked unsafe and the candidate
// baseline is NOT updated. Cells that pass commit to the candidate,
// so cell N can be marked unsafe because of cumulative effect of
// cells 1..N-1.
//
// "Worsens" predicate (matches the approved spec):
//   * before-ok && !after-ok                         (creates undercover)
//   * !before-ok && (after.headcount < before.headcount)   (worsens hc)
//   * !before-ok && (after.pct < before.pct - 0.5)         (worsens pct,
//                                                            with tiny
//                                                            FP epsilon)
//
// Cells that improve coverage OR have no measurable effect on any
// rule day are always safe.
//
// Rules with unsupported V1 fields (`max_*`, time-of-day window,
// location_id, effective dates) are IGNORED for safety decisions —
// matching the "false-green-safe" principle. Auto-Skip can never
// refuse a cell based on a rule the evaluator can't fully understand.
//
// Returns: { safe, unsafe, summarySkipped }
//   safe:   subset of toFlip whose application is safe
//   unsafe: subset of toFlip whose application is unsafe, each
//           augmented with `skipReason: string` describing the rule
//           + day that triggered the skip
//   summarySkipped: number — convenience count for the UI
export function computeRmiSafeCells({
  baselineCells, toFlip, rules, employees, categories, cycle_days,
  fromCategoryId, toCategoryId, teams, scope_team_id,
}) {
  if (!toFlip || toFlip.length === 0) {
    return { safe: [], unsafe: [], summarySkipped: 0 };
  }
  // TP-CG.team — same scope filter as `evaluateRotaStyleCoverage`.
  // RMI safety must not refuse a flip based on a rule that is out
  // of the current team scope.
  const scopedRules = (rules || []).filter((r) => {
    if (!r) return false;
    if (!scope_team_id) return true;
    if (!r.team_id) return true;
    return r.team_id === scope_team_id;
  });
  // Expand apply_per_team and drop unsupported rules upfront — they
  // never participate in the safety check.
  const distinctTeamIdsAll = Array.from(new Set(
    (employees || []).map((e) => e.team_id).filter(Boolean),
  ));
  const distinctTeamIds = scope_team_id
    ? [scope_team_id]
    : distinctTeamIdsAll;
  const teamNameById = new Map();
  for (const t of (teams || [])) {
    if (t?.team_id && t.name) teamNameById.set(t.team_id, t.name);
  }
  for (const e of (employees || [])) {
    if (e.team_id && e.team_name && !teamNameById.has(e.team_id)) {
      teamNameById.set(e.team_id, e.team_name);
    }
  }
  const activeCatIds = new Set(
    (categories || []).filter((c) => c.is_active !== false)
      .map((c) => c.category_id),
  );
  const safetyRules = [];
  for (const r of scopedRules) {
    if (!r.is_active) continue;
    if (ruleUsesUnsupportedV1Fields(r)) continue;
    if (!activeCatIds.has(r.category_id)) continue;
    if (r.apply_per_team) {
      for (const tid of distinctTeamIds) {
        safetyRules.push({
          ...r, team_id: tid, apply_per_team: false,
          _team_label: teamNameById.get(tid) || "Unknown team",
        });
      }
    } else {
      safetyRules.push(r);
    }
  }
  if (safetyRules.length === 0) {
    return { safe: toFlip.slice(), unsafe: [], summarySkipped: 0 };
  }

  const empById = new Map();
  for (const e of (employees || [])) empById.set(e.employee_id, e);
  // Kind map for the time-window path (Away rows never count as
  // work cover inside a window).
  const kindByCategoryId = new Map();
  for (const c of (categories || [])) {
    kindByCategoryId.set(c.category_id, c.kind || "work");
  }

  // Per-rule per-day measurement against a given cells array.
  function measureRuleDay(cells, rule, dayIdx) {
    const dayFilter = (rule.days_of_week && rule.days_of_week.length > 0)
      ? new Set(rule.days_of_week) : null;
    const dow = dayIdx % 7;
    if (dayFilter && !dayFilter.has(dow)) return null;
    const empSet = rule.team_id
      ? new Set((employees || [])
          .filter((e) => e.team_id === rule.team_id)
          .map((e) => e.employee_id))
      : null;
    let totalCellMins = 0;
    let categoryCellMins = 0;
    const assignedEmps = new Set();
    for (const c of cells) {
      if (c.cycle_day_index !== dayIdx) continue;
      if (empSet && !empSet.has(c.employee_id)) continue;
      const assigns = c.assignments || [];
      if (assigns.length === 0) continue;
      const cellMins = cellNetMinutes(c, empById);
      totalCellMins += cellMins;
      const perAssign = cellMins / assigns.length;
      for (const a of assigns) {
        if (a.category_id === rule.category_id) categoryCellMins += perAssign;
      }
      if (assigns.some((a) => a.category_id === rule.category_id)) {
        assignedEmps.add(c.employee_id);
      }
    }
    const pct = totalCellMins > 0
      ? (categoryCellMins / totalCellMins) * 100 : 0;
    const hcRule = rule.min_headcount !== null
      && rule.min_headcount !== undefined;
    const pctRule = rule.min_coverage_pct !== null
      && rule.min_coverage_pct !== undefined;
    const hcOk = hcRule ? assignedEmps.size >= rule.min_headcount : null;
    const pctOk = pctRule ? pct >= rule.min_coverage_pct : null;
    const ok = (hcOk === true) || (pctOk === true);
    return {
      headcount: assignedEmps.size, pct,
      ok, hcRule, pctRule,
    };
  }

  // CG time-window Phase 1 - measurement for flagged rules. Returns
  // null when the day is out of the rule's day filter or the rule
  // has no enforceable minimum.
  function measureWindowRuleDay(cells, rule, dayIdx) {
    const dayFilter = (rule.days_of_week && rule.days_of_week.length > 0)
      ? new Set(rule.days_of_week) : null;
    if (dayFilter && !dayFilter.has(dayIdx % 7)) return null;
    const mn = rule.min_headcount;
    if (mn === null || mn === undefined) return null;
    const empSet = rule.team_id
      ? new Set((employees || [])
          .filter((e) => e.team_id === rule.team_id)
          .map((e) => e.employee_id))
      : null;
    const employeeSegments = [];
    for (const c of cells) {
      if (c.cycle_day_index !== dayIdx) continue;
      if (empSet && !empSet.has(c.employee_id)) continue;
      const contrib = cgWindowCellContribution(
        c, kindByCategoryId, rule.category_id);
      if (contrib === null) continue;
      if (contrib.segments.length > 0) {
        employeeSegments.push(contrib.segments);
      }
    }
    return sweepWindowCoverage({
      windowStart: hhmmToMin(rule.start_time),
      windowEnd: hhmmToMin(rule.end_time),
      minHeadcount: mn, employeeSegments, ambiguousCount: 0,
    });
  }

  // Returns null if a cell's application would not worsen any rule
  // day; otherwise returns a short human-readable reason.
  function worseningReason(beforeCells, afterCells, touchedDayIdx, rule) {
    // CG time-window Phase 1 - flagged rules use the continuous
    // window measurement. Binary: a change that breaks a covered
    // window, or shrinks verifiable cover of an already-broken
    // window, is skipped.
    if (isTimeWindowRule(rule)) {
      const beforeW = measureWindowRuleDay(beforeCells, rule, touchedDayIdx);
      const afterW = measureWindowRuleDay(afterCells, rule, touchedDayIdx);
      if (beforeW === null || afterW === null) return null;
      const dayLbl = `day ${touchedDayIdx + 1}`;
      const teamLbl = rule._team_label ? ` (${rule._team_label})` : "";
      const catLbl = rule.category_name || rule.category_id;
      const winLbl = `${rule.start_time} to ${rule.end_time}`;
      if (beforeW.status === "ok" && afterW.status !== "ok") {
        return `Would leave ${catLbl}${teamLbl} not fully covered `
          + `between ${winLbl} on ${dayLbl}`;
      }
      if (beforeW.status !== "ok"
          && afterW.coveredOkMinutes < beforeW.coveredOkMinutes) {
        return `Would worsen ${catLbl}${teamLbl} cover `
          + `between ${winLbl} on ${dayLbl}`;
      }
      return null;
    }
    const before = measureRuleDay(beforeCells, rule, touchedDayIdx);
    const after = measureRuleDay(afterCells, rule, touchedDayIdx);
    if (before === null || after === null) return null;  // day out of window
    // 1. Creates undercoverage (was ok, now isn't).
    if (before.ok && !after.ok) {
      const dayLbl = `day ${touchedDayIdx + 1}`;
      const teamLbl = rule._team_label ? ` (${rule._team_label})` : "";
      return `Would put ${rule.category_name || rule.category_id}${teamLbl} `
        + `below the minimum on ${dayLbl}`;
    }
    // 2. Worsens existing undercoverage.
    if (!before.ok) {
      const hcWorse = before.hcRule && after.headcount < before.headcount;
      const pctWorse = before.pctRule && after.pct < before.pct - 0.5;
      if (hcWorse || pctWorse) {
        const dayLbl = `day ${touchedDayIdx + 1}`;
        const teamLbl = rule._team_label ? ` (${rule._team_label})` : "";
        const bits = [];
        if (hcWorse) bits.push(
          `${before.headcount} → ${after.headcount} people`);
        if (pctWorse) bits.push(
          `${Math.round(before.pct)}% → ${Math.round(after.pct)}%`);
        return `Would worsen ${rule.category_name || rule.category_id}${teamLbl} `
          + `on ${dayLbl} (${bits.join(", ")})`;
      }
    }
    return null;
  }

  // Apply a SINGLE cell change against a candidate cells array,
  // returning the new array. Mirrors the C3 kernel.
  function applyOneCell(cells, item) {
    const targetId = item.cell.cell_id;
    return cells.map((c) => {
      if (c.cell_id !== targetId) return c;
      const assigns = c.assignments || [];
      const alreadyHasTo = assigns.some(
        (a) => a.category_id === toCategoryId);
      if (alreadyHasTo) return c;
      if (item.action === "add") {
        const singleSlotFromOnly = (
          assigns.length === 1
          && assigns[0].category_id === fromCategoryId
        );
        if (!singleSlotFromOnly) return c;
        return {
          ...c,
          assignments: [
            ...assigns.map((a) => ({ category_id: a.category_id })),
            { category_id: toCategoryId },
          ],
        };
      }
      const containsFrom = assigns.some(
        (a) => a.category_id === fromCategoryId);
      if (!containsFrom) return c;
      let flipped = false;
      return {
        ...c,
        assignments: assigns.map((a) => {
          if (!flipped && a.category_id === fromCategoryId) {
            flipped = true;
            return { category_id: toCategoryId };
          }
          return { category_id: a.category_id };
        }),
      };
    });
  }

  let candidate = (baselineCells || []).slice();
  const safe = [];
  const unsafe = [];
  for (const item of toFlip) {
    const tentative = applyOneCell(candidate, item);
    const touchedDayIdx = item.cell.cycle_day_index;
    let reason = null;
    for (const rule of safetyRules) {
      const r = worseningReason(candidate, tentative, touchedDayIdx, rule);
      if (r) { reason = r; break; }
    }
    if (reason) {
      unsafe.push({ ...item, skipReason: reason });
    } else {
      safe.push(item);
      candidate = tentative;
    }
  }
  return { safe, unsafe, summarySkipped: unsafe.length };
}
